'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import * as React from 'react'
import { Formik, useFormikContext } from 'formik'
import Icon from '@mdi/react'
import { mdiBackspace } from '@mdi/js'

// walrus
import submitForm from '@lib/submit-form'
import Box, { BoxProps } from '@components/kit/box'
import Form, {
  SubmitButton,
  FormRow,
  TextField,
  StatusMessage
} from '@components/kit/form'
import { SearchIcon } from '@components/kit/icons'
import { colors } from '@theme'

/* -----------------------------------------------------------------------------
 * SearchBarForm
 * -------------------------------------------------------------------------- */

type SearchBarFormValues = {
  query: string
}

type SearchBarFormProps = {
  onPress: (query: string) => Promise<void>
}

const SearchBarForm = ({ onPress }: SearchBarFormProps) => {
  const { values, setFieldValue } = useFormikContext<SearchBarFormValues>()

  const handleClear = () => {
    setFieldValue('query', '')
    onPress('')
  }

  return (
    <Form width='100%'>
      <FormRow m='00'>
        <StatusMessage />
      </FormRow>
      <FormRow flexDirection='row' m='00' alignItems='center'>
        <TextField
          submitOnEnter
          name='query'
          label='Search by Name or Generated ID'
          flex='1 1 auto'
          fieldIcon={
            values.query ? (
              <Icon
                path={mdiBackspace}
                size='18px'
                color={(colors as Record<string, string>).shade06 ?? '#4B5563'}
              />
            ) : undefined
          }
          onIconPress={values.query ? handleClear : undefined}
        />
        <SubmitButton variant='punchThroughViolet' flex='0 0 auto'>
          <SearchIcon />
        </SubmitButton>
      </FormRow>
    </Form>
  )
}

/* -----------------------------------------------------------------------------
 * SearchBar
 * -------------------------------------------------------------------------- */

export type SearchBarProps = BoxProps & {
  initialQuery?: string
  onPress: (query: string) => Promise<void>
}

function SearchBar({ initialQuery = '', onPress, ...rest }: SearchBarProps) {
  const submit = submitForm(({ query }: SearchBarFormValues) => onPress(query))

  return (
    <Box {...rest}>
      <Formik<SearchBarFormValues>
        enableReinitialize
        initialValues={{ query: initialQuery }}
        onSubmit={submit}
      >
        <SearchBarForm onPress={onPress} />
      </Formik>
    </Box>
  )
}

export default SearchBar
